import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils';

interface SummaryItem {
  label: string;
  value: string | number | React.ReactNode;
}

interface SummarySection {
  title: string;
  items: SummaryItem[];
}

interface WizardSummaryProps {
  sections: SummarySection[];
  className?: string;
}

export function WizardSummary({ sections, className }: WizardSummaryProps) {
  return (
    <div className={cn('space-y-6', className)}>
      {sections.map((section, index) => (
        <div key={index}>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
            {section.title}
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {section.items.map((item, itemIndex) => (
                <div key={itemIndex}>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {item.label}
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white mt-0.5">
                    {item.value || '-'}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      ))}
    </div>
  );
}

interface WizardSuccessProps {
  title: string;
  message?: string;
  actions?: React.ReactNode;
  className?: string;
}

export function WizardSuccess({ title, message, actions, className }: WizardSuccessProps) {
  return (
    <div className={cn('text-center py-8', className)}>
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircleIcon className="w-10 h-10 text-green-500" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
      {message && (
        <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">{message}</p>
      )}
      {actions && <div className="mt-6 flex items-center justify-center gap-3">{actions}</div>}
    </div>
  );
}
